import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Mart { readonly martId: string; readonly martName: string; }

/** M26 — Self-Service Analytics Studio (§16/§23, BR-06). Run governed queries. */
export function M26SelfServiceStudioPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [marts, setMarts] = useState<Mart[]>([]);
  const [martId, setMartId] = useState('');
  const [sql, setSql] = useState('SELECT * FROM fact_invoices');
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: Mart[] }>('/v1/dwh/marts?publishedOnly=true', { token })
      .then((r) => { if (alive) { setMarts(r.data); if (r.data[0]) setMartId(r.data[0].martId); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function run(): Promise<void> {
    try {
      const r = await apiRequest<{ data: { rowCount: number } }>(`/v1/dwh/marts/${martId}/query`, { method: 'POST', body: { queryText: sql }, token });
      setRowCount(r.data.rowCount); setError(null);
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <main aria-label="self-service-studio">
      <h1>{t('m26.selfService.title')}</h1>
      <label htmlFor="studio-mart">{t('m26.selfService.martLabel')}</label>
      <select id="studio-mart" value={martId} onChange={(e) => setMartId(e.target.value)}>
        {marts.map((m) => <option key={m.martId} value={m.martId}>{m.martName}</option>)}
      </select>
      <label htmlFor="studio-sql">{t('m26.selfService.queryLabel')}</label>
      <textarea id="studio-sql" value={sql} onChange={(e) => setSql(e.target.value)} />
      <button type="button" onClick={run} disabled={!martId}>{t('m26.selfService.runQuery')}</button>
      {error && <p role="alert">{error}</p>}
      {rowCount !== null && <p aria-label="row-count">{t('m26.selfService.count', { count: rowCount })}</p>}
    </main>
  );
}
